import React from "react";
import { Badge, Box, Button, Checkbox, Heading, SimpleGrid, Stack, Text, Textarea } from "@chakra-ui/react";
import { OktonAlert, OktonInput, OktonLoadingState, OktonPanel, OktonSelect, useOktonPlatform } from "@okton/platform-ui";
import { dryRunRouter, executeTool, loadRouterCatalog, resolveRouter, RouterCatalog, RouterDryRunResult, RouterResolveResult, ToolExecutionResult } from "./aiRouterApi";

const tierLabel: Record<string,string> = { economic: "Econômico", standard: "Padrão", premium: "Premium" };

export const AiRouterPanel: React.FC<{ accessToken?: string | null }> = ({ accessToken }) => {
  const { applications } = useOktonPlatform();
  const [data,setData]=React.useState<RouterCatalog|null>(null);
  const [loading,setLoading]=React.useState(true);
  const [error,setError]=React.useState("");
  const [text,setText]=React.useState("Qual foi meu faturamento hoje?");
  const [outputTokens,setOutputTokens]=React.useState("200");
  const [requiresTools,setRequiresTools]=React.useState(false);
  const [requiresVision,setRequiresVision]=React.useState(false);
  const [requiresReasoning,setRequiresReasoning]=React.useState(false);
  const [critical,setCritical]=React.useState(false);
  const [simulating,setSimulating]=React.useState(false);
  const [executingTool,setExecutingTool]=React.useState(false);
  const [toolResult,setToolResult]=React.useState<ToolExecutionResult|null>(null);
  const [simulation,setSimulation]=React.useState<RouterDryRunResult|null>(null);
  const [resolveSystemId,setResolveSystemId]=React.useState("");
  const [resolving,setResolving]=React.useState(false);
  const [resolution,setResolution]=React.useState<RouterResolveResult|null>(null);
  const controllerRef=React.useRef<AbortController|null>(null);

  React.useEffect(()=>{
    if(resolveSystemId==="" && applications.length>0){
      const preferred=applications.find(application=>application.id==="iflex") || applications[0];
      if(preferred)setResolveSystemId(preferred.id);
    }
  },[applications,resolveSystemId]);

  React.useEffect(()=>{
    const controller=new AbortController();
    controllerRef.current=controller;
    setLoading(true); setError("");
    if(!accessToken){setLoading(false);setError("Sessão indisponível.");return()=>controller.abort();}
    loadRouterCatalog(accessToken,controller.signal)
      .then(value=>{if(!controller.signal.aborted)setData(value);})
      .catch(e=>{if(!controller.signal.aborted)setError(e instanceof Error?e.message:"Erro ao carregar.");})
      .finally(()=>{if(!controller.signal.aborted)setLoading(false);});
    return()=>controller.abort();
  },[accessToken]);

  async function simulate(){
    if(!accessToken||!controllerRef.current)return;
    const expectedOutputTokens=Number(outputTokens);
    if(!Number.isInteger(expectedOutputTokens)||expectedOutputTokens<1||expectedOutputTokens>20000||text.trim()===""){
      setError("Informe texto e quantidade de tokens de saída válidos.");
      return;
    }
    setError("");
    setSimulation(null);
    setToolResult(null);
    setSimulating(true);
    try{
      const result=await dryRunRouter(accessToken,controllerRef.current.signal,{
        text:text.trim(),requiresTools,requiresVision,requiresReasoning,critical,expectedOutputTokens
      });
      if(!controllerRef.current.signal.aborted)setSimulation(result);
    }catch(e){
      if(!controllerRef.current.signal.aborted)setError(e instanceof Error?e.message:"Erro ao simular.");
    }finally{
      if(!controllerRef.current.signal.aborted)setSimulating(false);
    }
  }

  async function resolveFullPipeline(){
    if(!accessToken||!controllerRef.current)return;
    const expectedOutputTokens=Number(outputTokens);
    if(resolveSystemId===""||!Number.isInteger(expectedOutputTokens)||expectedOutputTokens<1||expectedOutputTokens>20000||text.trim()===""){
      setError("Selecione o sistema e informe uma solicitação válida.");
      return;
    }
    setError("");
    setResolution(null);
    setResolving(true);
    try{
      const result=await resolveRouter(accessToken,controllerRef.current.signal,{
        systemId:resolveSystemId,
        capability:"assistant.text",
        text:text.trim(),
        requiresTools,
        requiresVision,
        requiresReasoning,
        critical,
        expectedOutputTokens
      });
      if(!controllerRef.current.signal.aborted)setResolution(result);
    }catch(e){
      if(!controllerRef.current.signal.aborted)setError(e instanceof Error?e.message:"Erro ao resolver pipeline.");
    }finally{
      if(!controllerRef.current.signal.aborted)setResolving(false);
    }
  }

  async function runTierZero(){
    if(!accessToken||!controllerRef.current||!simulation?.tierZero||!simulation.tierZero.ready)return;
    if(simulation.tierZero.tool.executionMode!=="read"){
      setError("Somente Tools read-only podem ser executadas por este painel.");
      return;
    }
    setError("");
    setToolResult(null);
    setExecutingTool(true);
    try{
      const result=await executeTool(
        accessToken,
        controllerRef.current.signal,
        simulation.tierZero.tool.code,
        simulation.tierZero.arguments
      );
      if(!controllerRef.current.signal.aborted)setToolResult(result);
    }catch(e){
      if(!controllerRef.current.signal.aborted)setError(e instanceof Error?e.message:"Erro ao executar a Tool.");
    }finally{
      if(!controllerRef.current.signal.aborted)setExecutingTool(false);
    }
  }

  if(loading)return <OktonLoadingState title="Carregando catálogo do Router..." minH="180px"/>;
  if(!data&&error)return <OktonAlert status="error"><Text>{error}</Text></OktonAlert>;
  if(!data)return null;

  const modelName=(id:number|null)=>id===null?"Não definido":data.models.find(m=>m.id===id)?.display_name||("Modelo #"+id);
  const knowledge=data.knowledge || { totalChunks:0, eligibleChunks:0, staleChunks:0, systems:[] };
  return <Stack spacing="6">
    <OktonAlert status={data.routerEnabled ? "warning" : "info"}>
      <Text><b>Router {data.routerEnabled ? "ATIVO" : "desativado"}.</b> {data.routerEnabled ? "O roteamento automático está liberado." : "O catálogo prepara a migração sem alterar as chamadas atuais da OpenAI."}</Text>
    </OktonAlert>
    <OktonPanel title="Gate de ativação" subtitle="O Router só pode ser ativado se todos os critérios de segurança estiverem aprovados">
      <Stack spacing="2">
        <Badge w="fit-content">{data.policy.canEnable ? "Pronto para ativação" : "Bloqueado"}</Badge>
        <Text fontSize="sm">Benchmark: {data.policy.benchmark.passedCases}/{data.policy.benchmark.activeCases} · score mínimo {data.policy.benchmark.minimumScore}</Text>
        <Text fontSize="sm">Intenções seguras: {data.policy.quality.safe}/{data.policy.quality.total}</Text>
        <Text fontSize="sm">Provedores saudáveis: {data.policy.providers.healthy}/{data.policy.providers.enabled}</Text>
        <Text fontSize="sm">Orçamento disponível: R$ {(data.policy.budget.availableMicros/1000000).toFixed(4)}</Text>
        <Text fontSize="sm">Contabilidade do plano: {data.policy.gates.planAccountingReady ? "pronta" : "pendente"}</Text>
        <Text fontSize="sm">Escopos do piloto: {data.pilot.enabledScopes}</Text>
        {data.pilot.items.map(item=><Text key={`${item.systemId}-${item.capability}-${item.companyId}-${item.branchId}`} fontSize="xs">{item.systemId} · {item.capability} · empresa {item.companyId} · filial {item.branchId}</Text>)}
        {data.policy.reasons.length>0 && <Text fontSize="sm">Bloqueios: {data.policy.reasons.join(" · ")}</Text>}
        {!data.routerEnabled && <Text fontSize="xs">O gate estar aprovado não ativa o Router automaticamente.</Text>}
      </Stack>
    </OktonPanel>

    <OktonPanel title="Simulador do Router" subtitle="Dry-run: decide perfil, modelo e custo sem chamar nenhum provedor">
      <Stack spacing="4">
        <Box>
          <Text fontSize="sm" mb="2">Solicitação</Text>
          <Textarea value={text} onChange={e=>setText(e.target.value)} maxLength={20000} minH="110px"/>
        </Box>
        <SimpleGrid columns={[1,2]} spacing="4">
          <OktonSelect name="router-system" label="Sistema" value={resolveSystemId} onChange={e=>setResolveSystemId(e.target.value)}>
            <option value="">Selecione o sistema</option>
            {applications.map(application=><option key={application.id} value={application.id}>{application.name}</option>)}
          </OktonSelect>
          <OktonInput name="router-output-tokens" label="Tokens de saída estimados" value={outputTokens} onChange={e=>setOutputTokens(e.target.value)} />
          <Stack spacing="2" pt="7">
            <Checkbox isChecked={requiresTools} onChange={e=>setRequiresTools(e.target.checked)}>Requer tools</Checkbox>
            <Checkbox isChecked={requiresVision} onChange={e=>setRequiresVision(e.target.checked)}>Requer visão</Checkbox>
            <Checkbox isChecked={requiresReasoning} onChange={e=>setRequiresReasoning(e.target.checked)}>Requer raciocínio avançado</Checkbox>
            <Checkbox isChecked={critical} onChange={e=>setCritical(e.target.checked)}>Operação crítica</Checkbox>
          </Stack>
        </SimpleGrid>
        <Box>
          <Stack direction={["column","row"]} spacing="2">
            <Button size="sm" colorScheme="blue" isLoading={simulating} onClick={simulate}>Simular decisão</Button>
            <Button size="sm" variant="outline" isLoading={resolving} onClick={resolveFullPipeline}>Resolver pipeline completo</Button>
          </Stack>
        </Box>
        {error && <OktonAlert status="error"><Text>{error}</Text></OktonAlert>}
        {simulation && <Box borderWidth="1px" rounded="md" p="4">
          <Stack spacing="2">
            <Heading size="sm">Resultado da simulação</Heading>
            <Text><b>Perfil solicitado:</b> {simulation.requestedProfile}</Text>
            <Text><b>Perfil efetivo:</b> {simulation.profile}</Text>
            <Text><b>Motivos:</b> {simulation.reasons.join(" · ")}</Text>
            {simulation.tierZero && <>
              <Text><b>Tier 0:</b> {simulation.tierZero.intent} → {simulation.tierZero.tool.code} ({simulation.tierZero.confidence}% confiança)</Text>
              <Text><b>Parâmetros:</b> {simulation.tierZero.ready ? JSON.stringify(simulation.tierZero.arguments) : "faltam parâmetros para executar"}</Text>
            </>}
            <Text><b>Modelo primário:</b> {simulation.primaryModel?.displayName || (simulation.tierZero ? "Não usa LLM" : "Nenhum modelo compatível")}</Text>
            <Text><b>Fallback:</b> {simulation.fallbackModel?.displayName || "Não definido"}</Text>
            <Text><b>Tokens estimados:</b> {simulation.inputTokensEstimate} entrada · {simulation.outputTokensEstimate} saída</Text>
            <Text><b>Custo estimado:</b> {simulation.estimatedCost ? "R$ "+(simulation.estimatedCost.brlMicros/1000000).toFixed(6) : "Indisponível"}</Text>
            <Text><b>Dentro do teto do perfil:</b> {simulation.withinProfileBudget===null?"não calculado":simulation.withinProfileBudget?"sim":"não"}</Text>
            <Text><b>Orçamento global:</b> {(simulation.budget.consumedPercent).toFixed(0)}% consumido · disponível R$ {(simulation.budget.availableMicros/1000000).toFixed(4)}</Text>
            <Text><b>Política adaptativa:</b> {simulation.budget.action} · cabe no saldo: {simulation.budget.withinGlobalBudget===null?"não calculado":simulation.budget.withinGlobalBudget?"sim":"não"}</Text>
            <Text><b>Confirmação:</b> {simulation.requiresConfirmation?"obrigatória":"não exigida"}</Text>
            {simulation.tierZero?.ready && simulation.tierZero.tool.executionMode==="read" && (
              <Box pt="2">
                <Button size="sm" colorScheme="blue" isLoading={executingTool} onClick={runTierZero}>Consultar dados reais</Button>
              </Box>
            )}
            <Badge w="fit-content">{simulation.wouldExecute?"Executaria":"DRY-RUN — nenhuma chamada enviada"}</Badge>
          </Stack>
        </Box>}
        {toolResult && (
          <OktonAlert status="success">
            <Stack spacing="1">
              <Text><b>Consulta:</b> {toolResult.tool}</Text>
              <Text><b>Contexto:</b> empresa {toolResult.context.companyId} · filial {toolResult.context.branchId}</Text>
              <Text fontSize="sm" whiteSpace="pre-wrap">{JSON.stringify(toolResult.data, null, 2)}</Text>
            </Stack>
          </OktonAlert>
        )}
        {resolution && (
          <Box borderWidth="1px" rounded="md" p="4">
            <Stack spacing="2">
              <Heading size="sm">Pipeline completo</Heading>
              <Text><b>Rota:</b> {resolution.route} · <b>Fonte:</b> {resolution.source}</Text>
              {resolution.reason && <Text><b>Motivo:</b> {resolution.reason}</Text>}
              {resolution.answer && <Text whiteSpace="pre-wrap"><b>Resposta:</b> {resolution.answer}</Text>}
              {resolution.response && <Text whiteSpace="pre-wrap"><b>Cache:</b> {JSON.stringify(resolution.response,null,2)}</Text>}
              {resolution.tool && <Text><b>Tool:</b> {resolution.tool.intent} → {resolution.tool.tool.code}</Text>}
              <Text><b>RAG:</b> {resolution.rag.length} trecho(s)</Text>
              {resolution.providerDecision?.primaryModel && <Text><b>Modelo indicado:</b> {resolution.providerDecision.primaryModel.displayName}</Text>}
              <Text><b>Próximo passo:</b> {resolution.next || (resolution.final ? "resposta final" : "nenhum")}</Text>
              {resolution.interactionId && <Text fontSize="xs"><b>Interação:</b> {resolution.interactionId}</Text>}
            </Stack>
          </Box>
        )}
      </Stack>
    </OktonPanel>

    <OktonPanel title="Provedores" subtitle={"Revisão "+data.revision+" · "+data.providers.length+" cadastrados"}>
      <SimpleGrid columns={[1,2,3]} spacing="4">
        {data.providers.map(p=><Box key={p.id} borderWidth="1px" rounded="md" p="4">
          <Stack spacing="1">
            <Heading size="sm">{p.name}</Heading>
            <Text fontSize="sm">{p.id} · {p.kind}</Text>
            <Badge w="fit-content">{p.enabled?"Ativo":"Inativo"}</Badge>
            <Text fontSize="xs">Circuit breaker: {p.circuit_state} · falhas consecutivas: {p.consecutive_failures}</Text>
            <Text fontSize="xs">Prioridade {p.priority}</Text>
          </Stack>
        </Box>)}
      </SimpleGrid>
    </OktonPanel>

    <OktonPanel title="Métricas" subtitle="Histórico consolidado de execução e economia por rota">
      <SimpleGrid columns={[1,2,4]} spacing="4">
        <Box borderWidth="1px" rounded="md" p="4">
          <Heading size="xs">Interações</Heading>
          <Text fontSize="2xl" fontWeight="700">{data.metrics.total}</Text>
          <Text fontSize="xs">Sucesso: {data.metrics.success}</Text>
        </Box>
        <Box borderWidth="1px" rounded="md" p="4">
          <Heading size="xs">Chamadas evitadas</Heading>
          <Text fontSize="2xl" fontWeight="700">{data.metrics.avoidedProviderCalls}</Text>
          <Text fontSize="xs">{data.metrics.providerAvoidancePercent.toFixed(1)}% das solicitações elegíveis não chegaram ao provedor</Text>
        </Box>
        <Box borderWidth="1px" rounded="md" p="4">
          <Heading size="xs">Chamadas ao provedor</Heading>
          <Text fontSize="2xl" fontWeight="700">{data.metrics.providerCalls}</Text>
          <Text fontSize="xs">Pendentes de conferência: {data.metrics.uncertainCalls}</Text>
        </Box>
        <Box borderWidth="1px" rounded="md" p="4">
          <Heading size="xs">Cache / latência</Heading>
          <Text fontSize="2xl" fontWeight="700">{data.metrics.cacheHits}</Text>
          <Text fontSize="xs">Latência média: {data.metrics.averageLatencyMs.toFixed(0)} ms</Text>
        </Box>
        <Box borderWidth="1px" rounded="md" p="4">
          <Heading size="xs">Rotas</Heading>
          {data.metrics.routes.length===0 ? <Text fontSize="sm">Sem histórico</Text> :
            data.metrics.routes.map(item=><Text key={item.route} fontSize="sm">{item.route}: {item.total}</Text>)}
        </Box>
      </SimpleGrid>
    </OktonPanel>

    <OktonPanel title="Custos" subtitle="Reserva, custo real e valor comercial separados">
      <SimpleGrid columns={[1,2,3]} spacing="4">
        <Box borderWidth="1px" rounded="md" p="4">
          <Heading size="xs">Reservado</Heading>
          <Text fontSize="xl" fontWeight="700">R$ {(data.cost.reservedMicros/1000000).toFixed(4)}</Text>
        </Box>
        <Box borderWidth="1px" rounded="md" p="4">
          <Heading size="xs">Custo provedor</Heading>
          <Text fontSize="xl" fontWeight="700">R$ {(data.cost.providerCostMicros/1000000).toFixed(4)}</Text>
          <Text fontSize="xs">Custo Okton: R$ {(data.cost.oktonCostMicros/1000000).toFixed(4)}</Text>
        </Box>
        <Box borderWidth="1px" rounded="md" p="4">
          <Heading size="xs">Cobrado ao cliente</Heading>
          <Text fontSize="xl" fontWeight="700">R$ {(data.cost.customerChargeMicros/1000000).toFixed(4)}</Text>
          <Text fontSize="xs">Liberado: R$ {(data.cost.releasedMicros/1000000).toFixed(4)}</Text>
        </Box>
      </SimpleGrid>
    </OktonPanel>

    <OktonPanel title="Cache" subtitle="Cache exato/normalizado com isolamento por escopo">
      <SimpleGrid columns={[1,2,3]} spacing="4">
        <Box borderWidth="1px" rounded="md" p="4">
          <Heading size="xs">Entradas ativas</Heading>
          <Text fontSize="2xl" fontWeight="700">{data.cache.activeEntries}</Text>
        </Box>
        <Box borderWidth="1px" rounded="md" p="4">
          <Heading size="xs">Hits acumulados</Heading>
          <Text fontSize="2xl" fontWeight="700">{data.cache.hits}</Text>
        </Box>
        <Box borderWidth="1px" rounded="md" p="4">
          <Heading size="xs">Escopos ativos</Heading>
          <Text fontSize="sm">Global: {data.cache.globalEntries}</Text>
          <Text fontSize="sm">Empresa: {data.cache.companyEntries}</Text>
          <Text fontSize="sm">Filial: {data.cache.branchEntries}</Text>
        </Box>
      </SimpleGrid>
    </OktonPanel>

    <OktonPanel title="Conhecimento / RAG" subtitle="Cobertura da documentação válida na revisão atual das fontes">
      <SimpleGrid columns={[1,2,3]} spacing="4">
        <Box borderWidth="1px" rounded="md" p="4">
          <Heading size="xs">Chunks totais</Heading>
          <Text fontSize="2xl" fontWeight="700">{knowledge.totalChunks}</Text>
        </Box>
        <Box borderWidth="1px" rounded="md" p="4">
          <Heading size="xs">Elegíveis agora</Heading>
          <Text fontSize="2xl" fontWeight="700">{knowledge.eligibleChunks}</Text>
        </Box>
        <Box borderWidth="1px" rounded="md" p="4">
          <Heading size="xs">Desatualizados</Heading>
          <Text fontSize="2xl" fontWeight="700">{knowledge.staleChunks}</Text>
        </Box>
      </SimpleGrid>
      <Stack spacing="1" mt="4">
        {knowledge.systems.length===0 ? <Text fontSize="sm">Nenhum conhecimento indexado.</Text> :
          knowledge.systems.map(item=><Text key={item.systemId} fontSize="sm">{item.systemId}: {item.eligibleChunks}/{item.totalChunks} elegíveis · {item.staleChunks} desatualizados</Text>)}
      </Stack>
    </OktonPanel>

    <OktonPanel title="Tool Registry" subtitle={data.tools.length+" ferramentas determinísticas cadastradas"}>
      <SimpleGrid columns={[1,2,3]} spacing="4">
        {data.tools.map(tool=><Box key={tool.code} borderWidth="1px" rounded="md" p="4">
          <Stack spacing="1">
            <Heading size="xs">{tool.name}</Heading>
            <Text fontSize="sm">{tool.code}</Text>
            <Text fontSize="xs">{tool.description}</Text>
            <Badge w="fit-content">{tool.executionMode}</Badge>
            <Text fontSize="xs">Custo IA: R$ {(tool.costMicros/1000000).toFixed(6)}</Text>
          </Stack>
        </Box>)}
      </SimpleGrid>
    </OktonPanel>

    <OktonPanel title="Modelos" subtitle="Modelos disponíveis para futura seleção automática">
      {data.models.length===0?<Text fontSize="sm">Nenhum modelo migrado ainda.</Text>:
      <SimpleGrid columns={[1,2]} spacing="4">
        {data.models.map(m=><Box key={m.id} borderWidth="1px" rounded="md" p="4">
          <Heading size="xs">{m.display_name}</Heading>
          <Text fontSize="sm">{m.provider_id} · {tierLabel[m.tier]||m.tier}</Text>
          <Text fontSize="xs">Tools: {m.supports_tools?"sim":"não"} · Visão: {m.supports_vision?"sim":"não"} · Reasoning: {m.supports_reasoning?"sim":"não"}</Text>
        </Box>)}
      </SimpleGrid>}
    </OktonPanel>

    <OktonPanel title="Perfis de execução" subtitle="Perfis que substituirão a escolha fixa de modelo por sistema">
      <SimpleGrid columns={[1,2]} spacing="4">
        {data.profiles.map(p=><Box key={p.id} borderWidth="1px" rounded="md" p="4">
          <Stack spacing="1">
            <Heading size="sm">{p.name}</Heading>
            <Text fontSize="sm">{p.description}</Text>
            <Text fontSize="xs">Primário: {modelName(p.primary_model_id)}</Text>
            <Text fontSize="xs">Fallback: {modelName(p.fallback_model_id)}</Text>
            <Text fontSize="xs">Teto: R$ {(p.max_cost_micros/1000000).toFixed(4)} · até {p.max_calls} chamadas</Text>
            {p.requires_confirmation && <Badge w="fit-content">Confirmação obrigatória</Badge>}
          </Stack>
        </Box>)}
      </SimpleGrid>
    </OktonPanel>
  </Stack>;
};
